import { Box } from '@mui/material';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import BetsPagination from './BetsPagination';
import CustomLoading from '../../components/custom/loading/CustomLoading';
import CustomLoadingError from '../../components/custom/loading/CustomLoadingError';
import useFetchActiveSeason from '../../components/hooks/useFetchActiveSeason';
import useFetchCurrentUser from '../../components/hooks/useFetchCurrentUser';
import { getActiveSeasonId } from '../admin/seasons/seasonsSlice';
import { selectActiveSeasonId } from '../admin/seasons/selectors';
import BetEditButtons from './BetEditButtons';
import { betEditPageRootSx } from './betsPageStyles';
import { getAllBets } from './betsSlice';
import CompleteBetCard from './CompleteBetCard';
import EmptyBetCard from './EmptyBetCard';
import OpenedBetCard from './OpenedBetCard';
import OpenedBetHiddenCard from './OpenedBetHiddenCard';
import { isBetDetailsHidden } from './knockoutBetPrivacy';
import { selectAllBets, selectTotalPages } from './selectors';

export default function BetEditList(): JSX.Element {
	const dispatch = useAppDispatch();
	const activeSeasonId = useAppSelector(selectActiveSeasonId);
	const allBets = useAppSelector(selectAllBets);
	const [loading, setLoading] = useState(true);
	const [loadingError, setLoadingError] = useState(false);
	const [page, setPage] = useState<number>(1);
	const totalPages = useAppSelector(selectTotalPages);

	useFetchActiveSeason(activeSeasonId);
	useFetchCurrentUser();

	const handlePageChange = (pageNumber: number): void => {
		setPage(pageNumber);
	};

	useEffect(() => {
		if (!activeSeasonId) {
			dispatch(getActiveSeasonId());
		}
	}, []);

	useEffect(() => {
		setLoading(true);
		if (activeSeasonId) {
			dispatch(getAllBets({ seasonId: activeSeasonId, pageNumber: page - 1 }))
				.then(() => {
					setLoading(false);
				})
				.catch(() => {
					setLoadingError(true);
					setLoading(false);
				});
		}
	}, [activeSeasonId, page]);

	return (
		<Box sx={betEditPageRootSx}>
			<Box sx={{ borderBottom: 1, textAlign: 'center', pb: 0.5, mb: 0, mx: 2}}>
				{t('editingBets')}
			</Box>

			<BetsPagination
				page={page}
				totalPages={totalPages}
				onPageChange={handlePageChange}
				navigationAriaLabel="editingBets"
			/>

			{loading ? (
				<CustomLoading />
			) : (
				<Box>
					{loadingError ? (
						<CustomLoadingError />
					) : (
						<Box>
							{allBets &&
								(() => {
									const sortedBets = [...allBets].sort((betA, betB) => {
										const dateA = betA.updatedAt
											? new Date(betA.updatedAt)
											: betA.betResultAddedAt
											? new Date(betA.betResultAddedAt)
											: new Date(betA.createdAt);
										const dateB = betB.updatedAt
											? new Date(betB.updatedAt)
											: betB.betResultAddedAt
											? new Date(betB.betResultAddedAt)
											: new Date(betB.createdAt);

										return dateB.getTime() - dateA.getTime();
									});

									return sortedBets.map((bet) => {
										return (
											<Box key={bet.id}>
												{bet.betStatus === 'OPENED' ? (
													isBetDetailsHidden(bet) ? (
														<OpenedBetHiddenCard bet={bet} />
													) : (
														<OpenedBetCard bet={bet} />
													)
												) : (
													<Box>
														{bet.betStatus === 'EMPTY' ? (
															<>
																<EmptyBetCard bet={bet} />
															</>
														) : (
															<Box>
																<CompleteBetCard bet={bet} />
															</Box>
														)}
													</Box>
												)}
												<BetEditButtons bet={bet} />
											</Box>
										);
									});
								})()}
						</Box>
					)}
				</Box>
			)}
		</Box>
	);
}
